import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Column,
  Container,
  Copy,
  Flex,
  Heading,
  Stack,
} from '@grubhubprod/gh-cookbook-react';
import { Row, Square } from '@grubhubprod/gh-cookbook-react/dist';
import { SignupStepFooter } from 'src/components/common/features/Signup/components/SignupStepFooter/SignupStepFooter';
import { useEnterpriseSignupStatus } from 'src/components/EnterpriseSignupStatusProvider/EnterpriseSignupStatusProvider';
import { EnterpriseSetupTable } from 'src/components/EnterpriseSetupTable/EnterpriseSetupTable';
import { IGroup, useGroupSearch } from 'src/utilities/useSearchHooks';
import { ManageEmployeesModal } from 'src/components/enterprise/set-up/steps/ManageEmployeesModal';
import { EnterpriseBackToHomeButton } from 'src/components/EnterpriseBackToHomeButton/EnterpriseBackToHomeButton';
import { useAnalytics } from 'src/components/AnalyticsProvider/AnalyticsProvider';

export function ManageEmployeesStep(): JSX.Element {
  const { goToPreviousStep, goToNextStep, corpId } =
    useEnterpriseSignupStatus();
  const [hasEmployeesInAtLeastOneGroup, setHasEmployeesInAtLeastOneGroup] =
    useState(false);
  const [
    showManageEmployeesModalByIdState,
    setShowManageEmployeesModalByIdState,
  ] = useState(null);
  const { sendTealiumPageView } = useAnalytics();

  useEffect(() => {
    sendTealiumPageView({
      pagegroup: 'corporate admin - signup flow',
      pagesubgroup: 'assign employees to groups',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Box marginTop="12">
        <Container variant="md">
          <Stack y="x-sm">
            <Flex justifyContent="space-between">
              <Heading variant="h3">Next, assign employees to groups</Heading>
              <EnterpriseBackToHomeButton />
            </Flex>
          </Stack>
          <Stack y="sm">
            <Copy variant="body">
              Manage access to meal budgets by adding or removing employees from
              a group. You can edit this from your corporate admin portal.
            </Copy>
          </Stack>
          <EnterpriseSetupTable<IGroup>
            corpId={corpId}
            searchPlaceholder="Search by group name"
            noResultsMessage="You don’t have any groups yet"
            searchResource={(corpId) => useGroupSearch(corpId, true)}
            resultsListCb={(resultsList) =>
              setHasEmployeesInAtLeastOneGroup(
                resultsList.some(({ diner_count }) => diner_count > 0)
              )
            }
            tableMaxHeight="520px"
            headerColumns={[
              {
                columnName: 'Group name',
                columnWidth: 3,
                gutter: 4,
              },
              { columnName: 'Description', columnWidth: 3, gutter: 4 },
              { columnName: 'Employees', columnWidth: 3, gutter: 4 },
            ]}
            rowElementRender={(
              group,
              isEditingRow,
              toggleEditState,
              updateSearch
            ) => (
              <Square inset="sm">
                <Row gutter={4}>
                  <Column gutter={4} phone={3}>
                    <Copy variant="body">{group.name}</Copy>
                  </Column>
                  <Column gutter={4} phone={3} className="ellipsisOverflow">
                    <Copy variant="body">{group.description}</Copy>
                  </Column>
                  <Column gutter={4} phone={3}>
                    <Copy
                      variant="body"
                      color={group.diner_count > 0 ? 'primaryText' : 'warning'}
                    >
                      {`${group.diner_count} employees`}
                    </Copy>
                  </Column>
                  <Column gutter={4} phone={3}>
                    <Flex alignItems="center" justifyContent="center">
                      <Button
                        className="tableActionButtons"
                        variant="tertiary"
                        onClick={() =>
                          setShowManageEmployeesModalByIdState({
                            group,
                            isEditingRow,
                            toggleEditState,
                            updateSearch,
                          })
                        }
                      >
                        Manage employees
                      </Button>
                    </Flex>
                  </Column>
                </Row>
              </Square>
            )}
            hideFooter
          />
        </Container>
      </Box>
      <SignupStepFooter containerSize="md">
        <Flex justifyContent="space-between">
          <Button
            variant="tertiary"
            buttonSize="medium"
            onClick={goToPreviousStep}
          >
            Back
          </Button>
          <Button
            disabled={!hasEmployeesInAtLeastOneGroup}
            variant="primary"
            buttonSize="medium"
            onClick={goToNextStep}
          >
            Save and continue
          </Button>
        </Flex>
      </SignupStepFooter>
      {showManageEmployeesModalByIdState && (
        <ManageEmployeesModal
          corpId={corpId}
          onClose={() => setShowManageEmployeesModalByIdState(null)}
          onSuccess={showManageEmployeesModalByIdState.updateSearch}
          group={showManageEmployeesModalByIdState.group}
        />
      )}
    </>
  );
}
